import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    LayoutDashboard, Table, Share2, BarChart2, Lightbulb, UploadCloud,
    UserCircle, LogOut, Plus, Edit2, Trash2, Home, Settings
} from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dataset, Dashboard, ChartConfig, DataRelationship, Insight, LayoutItem, StorySlide, View, Explanation } from './types';
import type { DataPrepStepWithMeta } from './services/dataPrepTypes';
import { invalidateCache } from './services/dataPrepEngine';
import { me as apiMe, setAuthToken, login as apiLogin, register as apiRegister, User } from './services/api';

import { GoogleButton } from './components/GoogleButton';
import { ErrorBoundary } from './components/ErrorBoundary';
import { parseCSV } from './services/dataService';
import { generateInsights, explainInsight, createStorySlide } from './services/geminiService';

// Extracted Components
import ChartBuilder from './components/ChartBuilder';
import DataManagementPage from './components/DataManagementPage';
import DataLoaderPage from './components/DataLoaderPage';
import DataRelationshipsPage from './components/DataRelationshipsPage';
import InsightsPage from './components/InsightsPage';
import DashboardView from './components/DashboardView';
import WelcomeScreen from './components/WelcomeScreen';
import { Modal, ExplanationModal, StorySlideModal } from './components/Modals';
import { ContextMenu, ContextMenuItem } from './components/ContextMenu';
import { TopBar } from './components/Layout/TopBar';
import { TermsOfService, PrivacyPolicy } from './components/LegalDocs';

const logoUrl = "/logonew.png";

type WorkspaceTab = 'home' | 'loader' | 'datasets' | 'relationships' | 'dashboards' | 'chart-builder' | 'insights' | 'settings';

const App = () => {
    const [path, setPath] = useState(window.location.pathname);
    const [view, setView] = useState<View>('welcome');
    const [activeTab, setActiveTab] = useState<WorkspaceTab>('home');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [authOpen, setAuthOpen] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [activeDatasetId, setActiveDatasetId] = useState<string | null>(null);
    const [dashboards, setDashboards] = useState<Dashboard[]>([]);
    const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
    const [charts, setCharts] = useState<ChartConfig[]>([]);
    const [editingChartId, setEditingChartId] = useState<string | null>(null);
    const [loaderInitialSourceType, setLoaderInitialSourceType] = useState<'file' | 'paste' | 'url' | undefined>(undefined);
    const [relationships, setRelationships] = useState<DataRelationship[]>([]);
    const [insights, setInsights] = useState<Insight[]>([]);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [insightsError, setInsightsError] = useState<string | null>(null);
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; title: string; message: string; confirmText: string; isDanger: boolean; onConfirm: () => void; } | null>(null);
    const [explainingInsight, setExplainingInsight] = useState<Insight | null>(null);
    const [explanation, setExplanation] = useState<Explanation | null>(null);
    const [storyInsight, setStoryInsight] = useState<Insight | null>(null);
    const [storySlide, setStorySlide] = useState<StorySlide | null>(null);
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authName, setAuthName] = useState('');
    const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
    const [authError, setAuthError] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);

    const isPro = currentUser?.subscription_tier === 'pro' || currentUser?.subscription_tier === 'enterprise';

    const navigate = (p: string) => {
        window.history.pushState({}, '', p);
        setPath(p);
    };

    useEffect(() => {
        const onPopState = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const isValidEmail = (e: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(e || '');

    const activeDataset = useMemo(() => datasets.find(d => d.id === activeDatasetId), [datasets, activeDatasetId]);
    const activeDashboard = useMemo(() => dashboards.find(d => d.id === activeDashboardId), [dashboards, activeDashboardId]);
    const chartToEdit = useMemo(() => editingChartId ? charts.find(c => c.id === editingChartId) : null, [charts, editingChartId]);

    // Handlers
    const handleAuthSubmit = async () => {
        try {
            setAuthLoading(true);
            setAuthError(null);
            if (!isValidEmail(authEmail)) { setAuthError('Enter a valid email address.'); return; }
            if (authMode === 'login') {
                const res = await apiLogin({ email: authEmail, password: authPassword });
                setAuthToken(res.access_token);
                setCurrentUser(res.user);
            } else {
                const res = await apiRegister({ email: authEmail, password: authPassword, display_name: authName || undefined });
                setAuthToken(res.access_token);
                setCurrentUser(res.user);
            }
            setAuthOpen(false);
        } catch (e: any) {
            setAuthError(e?.message || 'Authentication failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = useCallback(() => {
        setConfirmModal({
            isOpen: true,
            title: 'Logout',
            message: 'Are you sure you want to logout?',
            confirmText: 'Logout',
            isDanger: true,
            onConfirm: async () => {
                try { await fetch('/api/auth/logout', { method: 'POST' }); } catch { }
                setAuthToken(null);
                setCurrentUser(null);
                window.location.href = '/';
            }
        });
    }, []);

    const handleFileUpload = async (file: File) => {
        if (!currentUser) {
            alert('You must be logged in to upload datasets.');
            return;
        }
        try {
            const { data, schema } = await parseCSV(file);
            const newDataset: Dataset = {
                id: `ds-${Date.now()}`,
                name: file.name.split('.').slice(0, -1).join('.'),
                data: data,
                schema: schema,
                sampleRows: data.slice(0, 100),
                createdAt: new Date().toISOString(),
                ownerId: String(currentUser.id),
            };
            setDatasets(prev => [...prev, newDataset]);
            setActiveDatasetId(newDataset.id);
        } catch (error) {
            console.error('File upload error:', error);
            alert('Failed to upload file: ' + (Array.isArray(error) ? error.map(e => e.message).join(', ') : (error as any).message));
        }
    };

    const handleNewChart = () => {
        const newChart: ChartConfig = {
            id: `chart-${Date.now()}`,
            datasetIds: [],
            type: 'bar',
            title: 'Untitled Chart',
            createdAt: new Date().toISOString(),
            isDraft: true,
            xField: '',
            yField: '',
            aggregation: 'count',
            customization: {
                color: '#f59e0b', // Gold
                showLegend: true,
                showDataLabels: false,
                showGrid: true,
                useMultipleColors: true,
                paletteName: 'vibrant',
                lineWidth: 2,
                areaOpacity: 0.8,
                roundedBars: false,
                pointSize: 6,
                pointOpacity: 0.9,
                labelContent: 'auto',
                showQuadrants: false,
            }
        };
        setCharts(prev => [...prev, newChart]);
        setEditingChartId(newChart.id);
        setActiveTab('chart-builder');
    };

    const handleCreateChart = (chartConfig: Omit<ChartConfig, 'id' | 'createdAt'>) => {
        const newChart: ChartConfig = {
            ...chartConfig,
            id: `chart-${Date.now()}`,
            createdAt: new Date().toISOString(),
            isDraft: false
        };
        setCharts(prev => [...prev, newChart]);
        setEditingChartId(newChart.id);
        console.log("Chart saved successfully!");
    };

    const handleUpdateChart = (updatedChart: ChartConfig) => {
        const savedChart = { ...updatedChart, isDraft: false };
        setCharts(prev => prev.map(c => c.id === savedChart.id ? savedChart : c));
        console.log("Chart updated successfully!");
    };

    const handleDeleteChart = (chartId: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Chart',
            message: 'Are you sure you want to delete this chart?',
            confirmText: 'Delete',
            isDanger: true,
            onConfirm: () => {
                setCharts(prev => prev.filter(c => c.id !== chartId));
                setEditingChartId(null);
            }
        });
    };

    const handleCreateDashboard = () => {
        const newDashboard: Dashboard = {
            id: `dash-${Date.now()}`,
            title: 'New Dashboard',
            layout: [],
            createdAt: new Date().toISOString(),
        };
        setDashboards(prev => [...prev, newDashboard]);
        setActiveDashboardId(newDashboard.id);
        setActiveTab('dashboards');
    };

    const handleAddChartToDashboard = (chartId: string, dashboardId: string, position?: { x: number, y: number }) => {
        setDashboards(prev => prev.map(d => {
            if (d.id !== dashboardId) return d;
            const newLayoutItem: LayoutItem = {
                i: `widget-${Date.now()}`,
                type: 'chart',
                chartId,
                x: position?.x ?? 0,
                y: position?.y ?? 0,
                w: 6,
                h: 4,
            };
            return { ...d, layout: [...d.layout, newLayoutItem] };
        }));
    };

    const handleUpdateDashboardLayout = (newLayout: LayoutItem[]) => {
        if (!activeDashboardId) return;
        setDashboards(prev => prev.map(d => d.id === activeDashboardId ? { ...d, layout: newLayout } : d));
    };

    const handleUpdateDashboardDetails = (details: Partial<Pick<Dashboard, 'title' | 'description'>>) => {
        if (!activeDashboardId) return;
        setDashboards(prev => prev.map(d => d.id === activeDashboardId ? { ...d, ...details } : d));
    };

    const handleDeleteDashboard = (dashboardId: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Dashboard',
            message: 'Are you sure you want to delete this dashboard?',
            confirmText: 'Delete',
            isDanger: true,
            onConfirm: () => {
                setDashboards(prev => prev.filter(d => d.id !== dashboardId));
                if (activeDashboardId === dashboardId) setActiveDashboardId(null);
            }
        });
    };

    const handleGenerateInsights = async () => {
        if (!activeDataset || !activeDataset.data) return;
        setInsightsLoading(true);
        setInsightsError(null);
        try {
            const result = await generateInsights(activeDataset.schema, activeDataset.data);
            setInsights(result);
        } catch (err) {
            setInsightsError("Failed to generate insights.");
        } finally {
            setInsightsLoading(false);
        }
    };

    const handleExplainInsight = async (insight: Insight) => {
        setExplainingInsight(insight);
        try {
            const explanation = await explainInsight(insight);
            setExplanation(explanation);
        } catch (err) {
            setExplanation({ title: "Error", meaning: "Failed to explain", importance: "", nextSteps: "" });
        }
    };

    const handleCreateStoryFromInsight = async (insight: Insight) => {
        setStoryInsight(insight);
        try {
            const slide = await createStorySlide(insight);
            setStorySlide(slide);
        } catch (err) {
            setStorySlide(null);
        }
    };

    const handleGenerateChartFromInsight = (insight: Insight) => {
        const newChart: ChartConfig = {
            id: `chart-${Date.now()}`,
            datasetIds: [activeDatasetId!],
            type: 'bar',
            title: insight.title,
            createdAt: new Date().toISOString(),
            isDraft: false,
            customization: {
                showLegend: true,
                showDataLabels: false,
                showGrid: true,
                useMultipleColors: true,
                paletteName: 'vibrant'
            }
        };
        setCharts(prev => [...prev, newChart]);
        setEditingChartId(newChart.id);
        setActiveTab('chart-builder');
    };

    const handleAddChartToDashboardFromInsight = (insight: Insight) => {
        if (!activeDashboardId) {
            handleCreateDashboard();
        }
        handleGenerateChartFromInsight(insight);
    };

    const handleDataPrepStepsChange = useCallback((datasetId: string, steps: DataPrepStepWithMeta[]) => {
        setDatasets(prev => prev.map(d =>
            d.id === datasetId
                ? { ...d, prepSteps: steps, updatedAt: new Date().toISOString() }
                : d
        ));
        invalidateCache(datasetId);
    }, []);

    const handleNavigateToLoader = (sourceType?: 'file' | 'paste' | 'url') => {
        setLoaderInitialSourceType(sourceType);
        setActiveTab('loader');
        setView('workspace');
    };

    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        // Cleanup legacy local storage token (we now use HttpOnly cookies)
        localStorage.removeItem('weaverbi_token');

        const initAuth = async () => {
             // Check for token in URL (Google OAuth redirect)
             const params = new URLSearchParams(window.location.search);
             const token = params.get('token');
             if (token) {
                 // Token is already set in HttpOnly cookie by the backend redirect
                 // We just clean the URL here
                 window.history.replaceState({}, '', window.location.pathname);
             }

             // Race actual auth check with a 3-second timeout so user isn't stuck forever
             const authPromise = async () => {
                 try {
                     const user = await apiMe();
                     setCurrentUser(user);
                 } catch (e) {
                     console.log("Not logged in or API unavailable");
                     setAuthOpen(true);
                 }
             };

             const timeoutPromise = new Promise(resolve => setTimeout(resolve, 3000));

             await Promise.race([authPromise(), timeoutPromise]);
             setIsInitializing(false);
         };
         initAuth();
    }, []);

    // Sidebar Component
    const SidebarItem = ({ icon, label, id }: { icon: React.ReactNode, label: string, id: WorkspaceTab }) => (
        <motion.button
            whileHover={{ scale: 1.05, x: 2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(id)}
            className={`group relative p-2.5 rounded-xl transition-all duration-300 ${activeTab === id
                ? 'bg-neon-gold/20 text-neon-gold shadow-glow'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
        >
            {icon}
            <span className="absolute left-full ml-4 px-2 py-1 bg-black/80 backdrop-blur border border-white/10 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {label}
            </span>
        </motion.button>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'home':
                return (
                    <WelcomeScreen
                        onNavigateToLoader={handleNavigateToLoader}
                        datasets={datasets}
                        onSelectDataset={(id) => {
                            setActiveDatasetId(id);
                            setActiveTab('datasets');
                        }}
                    />
                );
            case 'loader':
                return (
                    <DataLoaderPage
                        existingDatasets={datasets}
                        initialSourceType={loaderInitialSourceType}
                        onSaveDataset={(newDataset) => {
                            setDatasets(prev => [...prev, newDataset]);
                            setActiveDatasetId(newDataset.id);
                            setActiveTab('datasets');
                        }}
                    />
                );
            case 'datasets':
                return (
                    <DataManagementPage
                        datasets={datasets}
                        activeDatasetId={activeDatasetId}
                        setActiveDatasetId={setActiveDatasetId}
                        onDeleteDataset={(id) => {
                            setDatasets(prev => prev.filter(d => d.id !== id));
                            if (activeDatasetId === id) setActiveDatasetId(null);
                        }}
                        onNavigateToLoader={handleNavigateToLoader}
                        insights={insights}
                        insightsLoading={insightsLoading}
                        insightsError={insightsError}
                        onGenerateInsights={handleGenerateInsights}
                        onGenerateChartFromInsight={handleGenerateChartFromInsight}
                        onAddChartToDashboardFromInsight={handleAddChartToDashboardFromInsight}
                        onExplainInsight={handleExplainInsight}
                        onCreateStoryFromInsight={handleCreateStoryFromInsight}
                        relationships={relationships}
                        setRelationships={setRelationships}
                        onDataPrepStepsChange={handleDataPrepStepsChange}
                    />
                );
            case 'relationships':
                return (
                    <DataRelationshipsPage
                        datasets={datasets}
                        relationships={relationships}
                        setRelationships={setRelationships}
                    />
                );
            case 'dashboards':
                return (
                    <DashboardView
                        activeDashboard={activeDashboard}
                        allDashboards={dashboards}
                        allCharts={charts}
                        datasets={datasets}
                        relationships={relationships}
                        onSelectDashboard={(id) => setActiveDashboardId(id)}
                        onCreateDashboard={handleCreateDashboard}
                        onUpdateDashboardLayout={handleUpdateDashboardLayout}
                        onUpdateDashboardDetails={handleUpdateDashboardDetails}
                        onRemoveWidgetFromDashboard={(id) => {
                            if (!activeDashboardId) return;
                            setDashboards(prev => prev.map(d => d.id === activeDashboardId ? { ...d, layout: d.layout.filter(i => i.i !== id) } : d));
                        }}
                        onAddChartToDashboard={handleAddChartToDashboard}
                        onAddTextWidget={(dashId, pos) => {
                            setDashboards(prev => prev.map(d => {
                                if (d.id !== dashId) return d;
                                return {
                                    ...d, layout: [...d.layout, {
                                        i: `text-${Date.now()}`, type: 'text', content: 'New Text',
                                        x: pos?.x || 0, y: pos?.y || 0, w: 4, h: 2
                                    }]
                                };
                            }));
                        }}
                        onAddLineWidget={(dashId, orientation, pos) => {
                            setDashboards(prev => prev.map(d => {
                                if (d.id !== dashId) return d;
                                return {
                                    ...d, layout: [...d.layout, {
                                        i: `line-${Date.now()}`,
                                        type: 'line',
                                        lineOrientation: orientation,
                                        x: pos?.x || 0,
                                        y: pos?.y || 0,
                                        w: orientation === 'horizontal' ? 6 : 1,
                                        h: orientation === 'horizontal' ? 1 : 4
                                    }]
                                };
                            }));
                        }}
                        onDeleteDashboard={handleDeleteDashboard}
                    />
                );
            case 'chart-builder':
                return (
                    <ChartBuilder
                        datasets={datasets}
                        relationships={relationships}
                        onCreateChart={handleCreateChart}
                        chartToEdit={chartToEdit}
                        onUpdateChart={handleUpdateChart}
                        onDeleteChart={handleDeleteChart}
                        allCharts={charts}
                        onSelectChart={(id) => setEditingChartId(id)}
                    />
                );
            case 'insights':
                return (
                    <InsightsPage
                        insights={insights}
                        isLoading={insightsLoading}
                        error={insightsError}
                        onGenerate={handleGenerateInsights}
                        onGenerateChart={handleGenerateChartFromInsight}
                        onAddToDashboard={handleAddChartToDashboardFromInsight}
                        onExplain={handleExplainInsight}
                        onCreateStory={handleCreateStoryFromInsight}
                    />
                );
            case 'settings':
                return <div className="p-8 text-center text-gray-400">Settings Page Placeholder</div>;
            default:
                return null;
        }
    };

    if (isInitializing) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-[#0f172a] text-neon-gold">
                <div className="animate-pulse flex flex-col items-center gap-4">
                    <img src={logoUrl} alt="Loading..." className="w-16 h-16 object-contain" />
                    <span className="text-xl font-bold tracking-widest">LOADING WEAVER<span className="text-white">BI</span></span>
                </div>
            </div>
        );
    }

    // Legal Pages Routing
    if (path === '/terms') {
        return <TermsOfService onBack={() => navigate('/')} />;
    }
    if (path === '/privacy') {
        return <PrivacyPolicy onBack={() => navigate('/')} />;
    }

    return (
        <div className="flex h-screen bg-transparent text-white font-sans selection:bg-neon-gold/30 overflow-hidden">
            {/* Sidebar */}
            <aside className="w-16 flex flex-col items-center py-4 glass-panel m-3 rounded-xl z-50">
                <div className="mb-4">
                    <img src={logoUrl} alt="WeaverBI" className="w-8 h-8 object-contain" />
                </div>

                <div className="flex flex-col gap-3 w-full px-2">
                    <SidebarItem icon={<Home size={20} />} label="Home" id="home" />
                    <div className="w-6 h-[1px] bg-white/10 mx-auto my-1" />
                    <SidebarItem icon={<UploadCloud size={20} />} label="Data Loader" id="loader" />
                    <SidebarItem icon={<Table size={20} />} label="Datasets" id="datasets" />
                    <SidebarItem icon={<Share2 size={20} />} label="Relationships" id="relationships" />
                    <div className="w-6 h-[1px] bg-white/10 mx-auto my-1" />
                    <SidebarItem icon={<BarChart2 size={20} />} label="Chart Builder" id="chart-builder" />
                    <SidebarItem icon={<LayoutDashboard size={20} />} label="Dashboards" id="dashboards" />
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 relative">
                <TopBar onLogout={handleLogout} />
                <main className="flex-1 overflow-hidden relative px-0 pb-0">
                    <div className="h-full w-full overflow-y-auto">
                        {renderContent()}
                    </div>
                </main>
            </div>

            {/* Modals */}
            <Modal isOpen={!!confirmModal} onClose={() => setConfirmModal(null)} title={confirmModal?.title || ''} maxWidth="max-w-sm">
                <div className="space-y-4">
                    <p className="text-gray-300">{confirmModal?.message}</p>
                    <div className="flex justify-end gap-3">
                        <button onClick={() => setConfirmModal(null)} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => { confirmModal?.onConfirm(); setConfirmModal(null); }} className={`px-4 py-2 rounded-lg font-bold ${confirmModal?.isDanger ? 'bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white' : 'bg-neon-gold/20 border border-neon-gold text-neon-gold hover:bg-neon-gold hover:text-white'} transition-all shadow-lg`}>
                            {confirmModal?.confirmText}
                        </button>
                    </div>
                </div>
            </Modal>

            <ExplanationModal explanation={explanation} onClose={() => setExplanation(null)} />
            <StorySlideModal slide={storySlide} onClose={() => setStorySlide(null)} />

            {/* Auth Modal */}
            {(authOpen) && (
                <Modal isOpen={authOpen} onClose={() => setAuthOpen(false)} title="Welcome to WeaverBI" maxWidth="max-w-md">
                    <div className="space-y-6 p-4">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-neon-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-slow">
                                <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sign In</h3>
                            <p className="text-gray-400 text-sm">Connect your account to save dashboards.</p>
                        </div>

                        <GoogleButton fullWidth />

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-700"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-[#0f172a] text-gray-500">Or continue with email</span>
                            </div>
                        </div>

                        <div className="space-y-3 opacity-50 hover:opacity-100 transition-opacity">
                            <input type="email" placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-gold focus:outline-none focus:ring-1 focus:ring-neon-gold transition-all" />
                            <input type="password" placeholder="Password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-gold focus:outline-none focus:ring-1 focus:ring-neon-gold transition-all" />
                            {authMode === 'register' && <input type="text" placeholder="Name" value={authName} onChange={e => setAuthName(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-gold focus:outline-none focus:ring-1 focus:ring-neon-gold transition-all" />}
                            {authError && <p className="text-red-400 text-sm">{authError}</p>}
                            <button onClick={handleAuthSubmit} disabled={authLoading} className="w-full p-3 bg-neon-gold/10 border border-neon-gold/50 text-neon-gold font-bold rounded-lg hover:bg-neon-gold hover:text-white transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)]">
                                {authLoading ? 'Loading...' : authMode === 'login' ? 'Sign In with Email' : 'Sign Up with Email'}
                            </button>
                            <button onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} className="w-full text-sm text-gray-500 hover:text-gray-300">
                                {authMode === 'login' ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
                            </button>

                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-[#0f172a] text-gray-500">Development</span>
                                </div>
                            </div>

                            <button onClick={() => setAuthOpen(false)} className="w-full p-2 bg-white/5 border border-white/10 text-gray-400 rounded-lg hover:bg-white/10 hover:text-white transition-all text-sm">
                                Continue as Guest
                            </button>

                            <div className="flex justify-center gap-4 text-xs text-gray-500 mt-4 pt-4 border-t border-white/5">
                                <button onClick={() => navigate('/terms')} className="hover:text-gray-300 transition-colors">Terms of Service</button>
                                <span>•</span>
                                <button onClick={() => navigate('/privacy')} className="hover:text-gray-300 transition-colors">Privacy Policy</button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Context Menu */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenu.items}
                    onClose={() => setContextMenu(null)}
                />
            )}
            <Analytics />
        </div>
    );
};

export default App;
